import * as React from "react";
import { useForm, useFieldArray, Control, useWatch } from "react-hook-form";
import {
  Button,
  Grid,
  Title,
  ActionIcon,
  Group,
  useMantineTheme,
  Flex,
  Stack,
  Divider,
} from "@mantine/core";
import dayjs from "dayjs";

import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { IconPlus, IconTrash, IconX } from "@tabler/icons-react";

import { RoleSchema, RoleType } from "@supportinitiative/common";

import {
  CSelect,
  CTextInput,
  CDatePicker,
  CMultiSelect,
  CTextarea,
} from "@supportinitiative/react-hook-form-mantine";

import { styles } from "../styles";
import { generateApiKey, postRole } from "../api";
import { AuditType, IDispatchAction } from "../types";
import { getActionOptions } from "./RoleUtils";
import { UserContext } from "../misc/UserContext";
import { useActiveRoles, useAdapters, useRole } from "../hooks";
import FormSubmitError from "../ui/Error/FormSubmitError";
import FormStateError from "../ui/Error/FormStateError";
import Success from "../ui/Success";
import Audit from "../ui/Audit/Audit";
import { getId, showError } from "../utilities";

type actionTypes =
  | "CURRENT-ACTION"
  | "ACTION-OPTIONS"
  | "SET-VALUE"
  | "SET-SUBMITTING"
  | "SET-SUCCESS"
  | "SET-ERROR";

export const RoleCVMContext = React.createContext<{
  state: any;
  dispatch: React.Dispatch<IDispatchAction<actionTypes>>;
}>({
  state: {},
  dispatch: () => {},
});

const initialState = {
  currentAction: "NEW",
  entityId: "",
  actionOptions: {},
  setValue: () => {},
  submitting: false,
  error: "",
  successMessage: "",
};

function reducer(state: any, action: IDispatchAction<actionTypes>) {
  switch (action.type) {
    case "CURRENT-ACTION":
      return { ...state, currentAction: action.value };
    case "ACTION-OPTIONS":
      return { ...state, actionOptions: action.value };
    case "SET-VALUE":
      return { ...state, setValue: action.value };
    case "SET-SUBMITTING":
      return { ...state, submitting: action.value };
    case "SET-SUCCESS":
      return { ...state, successMessage: action.value };
    case "SET-ERROR":
      return { ...state, error: action.value };
    default:
      return state;
  }
}

export const getPoliciesLOV = (
  policies: {
    id: string;
    name: string;
    displayName: string;
  }[]
) => {
  let policiesLOV = (policies || []).map((policy) => {
    return {
      label: `${policy.name} [ ${policy.displayName} ]` || policy.name,
      value: policy.name,
    };
  });
  return policiesLOV;
};

function getAccessTypeOptions(type: string) {
  return [
    { label: "View", value: "VIEW" },
    { label: "Create", value: "CREATE" },
  ];
}

function AccessControl({
  name,
  index,
  accessArray,
  control,
  roleFormState,
}: {
  name: `access.${number}`;
  index: number;
  accessArray: any;
  control: Control<any>;
  roleFormState: any;
}) {
  const type = useWatch({
    control,
    name: `${name}.type`,
  });

  return (
    <Grid align="stretch" gutter={"xs"} mb="xs">
      <Grid.Col span={2}>
        <CSelect
          name={`${name}.type`}
          control={control}
          data={[
            { label: "Dataset", value: "DATASET" },
            { label: "Relationship", value: "RELATIONSHIP" },
            { label: "Reconset", value: "RECONSET" },
            { label: "Use case", value: "USECASE" },
            { label: "Loader", value: "LOADER" },
            { label: "Adapter", value: "ADAPTER" },
            { label: "Function", value: "FUNCTION" },
            { label: "User", value: "USER" },
            { label: "Role", value: "ROLE" },
            { label: "Policy", value: "POLICY" },
          ]}
          showErrorText={false}
          required
          disabled={roleFormState.actionOptions.disableAll}
        />
      </Grid.Col>
      <Grid.Col span={5}>
        <CMultiSelect
          name={`${name}.options`}
          control={control}
          data={getAccessTypeOptions(type)}
          showErrorText={false}
          required
          disabled={roleFormState.actionOptions.disableAll}
        />
      </Grid.Col>

      <Grid.Col span={1} style={{ display: "flex", alignItems: "flex-end" }}>
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={() => {
            accessArray.remove(index);
          }}
          style={{
            display:
              roleFormState.currentAction.toUpperCase() === "VIEW"
                ? "none"
                : "",
          }}
          disabled={roleFormState.actionOptions.disableAll}
        >
          <IconTrash />
        </ActionIcon>
      </Grid.Col>
    </Grid>
  );
}

export default function RoleCVM() {
  // let [dataset, setRole] = React.useState<RoleType>();

  let [audit, setAudit] = React.useState<AuditType | undefined>();

  // let [currentAction, setCurrentAction] = React.useState("new");
  let navigate = useNavigate();
  let [searchParams] = useSearchParams();
  let id = searchParams.get("id"),
    entityId = searchParams.get("entityId") as string;
  const { state: applicationState } = React.useContext(UserContext);
  const queryClient = useQueryClient();
  const theme = useMantineTheme();

  const { data: dataset } = useRole(entityId, id);
  // let [newRole, setNewRole] = React.useState<RoleType | undefined>();
  const [roleFormState, roleFormDispatch] = React.useReducer(reducer, {
    ...initialState,
    actionOptions: getActionOptions("new", dataset, dataset, applicationState),
  });
  // console.log("dataset in component", dataset);

  const { data: policies, isLoading } = useAdapters(entityId, "POLICY");

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<RoleType>({
    defaultValues: {
      id: "",
      name: "",
      description: "",
      type: "ROLE",
      access: [],
      definition: {
        policies: [],
      },
    },
    criteriaMode: "all",
    resolver: yupResolver(RoleSchema),
  });

  const accessArray = useFieldArray({
    control,
    name: "access",
    keyName: "__key",
  });

  /* 
  const columns = useWatch({
    control,
    name: "definition.columns",
  }); */

  // const [columns, setColumns] = React.useState<RoleType["definition"]["columns"]>([]);
  const onOk = () => {
    navigate(`/app/roles?entityId=${entityId}`);
  };

  React.useEffect(() => {
    if (dataset) {
      roleFormDispatch({ type: "CURRENT-ACTION", value: "VIEW" });
      roleFormDispatch({
        type: "ACTION-OPTIONS",
        value: getActionOptions("view", dataset, dataset, applicationState),
      });

      // setColumns(dataset.definition.columns || []);

      setAudit({
        status: dataset.status,
        authStatus: dataset.authStatus,
        usableStatus: dataset.usableStatus,
        makerId: dataset.makerId,
        makerDate: dataset.makerDate,
        modificationNo: dataset.modificationNo,
        checkerId: dataset.checkerId,
        checkerDate: dataset.checkerDate,
      });

      reset(dataset);
    }
  }, [dataset]);

  const onSubmit = async (data: any) => {
    try {
      roleFormDispatch({
        type: "SET-SUBMITTING",
        value: true,
      });
      roleFormDispatch({
        type: "SET-ERROR",
        value: "",
      });
      let payload = {
        ...data,
        entityId,
        metadata: {
          action: roleFormState.currentAction,
        },
      };
      await postRole(entityId, payload);
      // setError("Form", "Unable to Submit");
      roleFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      queryClient.invalidateQueries(["roles", entityId]);
      roleFormDispatch({
        type: "SET-SUCCESS",
        value: "Role saved successfully",
      });
    } catch (error: any) {
      // console.log("post catch", error);
      // console.log("post catch", error?.response?.data?.message);
      roleFormDispatch({
        type: "SET-SUBMITTING",
        value: false,
      });
      roleFormDispatch({
        type: "SET-ERROR",
        value: error?.response?.data?.message || "Unable to save role",
      });
    }
  };

  return (
    <RoleCVMContext.Provider
      value={{ state: roleFormState, dispatch: roleFormDispatch }}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ marginTop: theme.spacing.lg }}
        noValidate
      >
        <div id="header">
          <Grid
            align="flex-end"
            gutter={"xs"}
            mt="xs"
            mb="xs"
            // mb="xs"
            style={{
              display: "flex",
              // borderLeft: `${theme.spacing.xxs} solid ${theme.colors.emerald[7]}`,
            }}
            columns={12}
          >
            <Grid.Col span={3}>
              <Flex align={"end"} gap={theme.spacing.xs}>
                <Stack gap={0}>
                  <Title order={3} c="dark">
                    Role
                  </Title>
                </Stack>
              </Flex>
            </Grid.Col>
          </Grid>
        </div>

        <Divider size="sm" mb="xs" color="dark.2" />

        <div id="body">
          <Grid align="flex-start" gutter={"xs"} mb="xs">
            <Grid.Col span={4}>
              <CTextInput
                name="name"
                control={control}
                label="Name"
                aria-label="Name"
                required
                showErrorText={false}
                disabled={roleFormState.actionOptions.disableAll}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <CTextInput
                name="displayName"
                control={control}
                label="Display Name"
                aria-label="Display Name"
                required
                showErrorText={false}
                disabled={roleFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>

          <Grid
            align="flex-start"
            gutter={"xs"}
            mb="lg"
            style={{ display: "flex", alignItems: "flex-end" }}
          >
            <Grid.Col span={8}>
              <CTextarea
                name="description"
                control={control}
                label="Description"
                autosize
                minRows={2}
                maxRows={20}
                required
                showErrorText={false}
                disabled={roleFormState.actionOptions.disableAll}
              />
            </Grid.Col>
          </Grid>

          <Stack gap="0" mb="lg">
            <Group mb="xs" gap={"xs"} style={{ alignItems: "center" }}>
              <Flex>
                <Title
                  order={5}
                  style={{
                    padding: "0px 5px",
                    backgroundColor: "#16A596",
                    color: "#fff",
                  }}
                >
                  Access
                </Title>
              </Flex>

              <ActionIcon
                variant="light"
                size="lg"
                onClick={() => {
                  accessArray.append({
                    type: "",
                    options: [],
                  });
                }}
                style={{
                  display:
                    roleFormState.currentAction.toUpperCase() === "VIEW"
                      ? "none"
                      : "",
                }}
                disabled={roleFormState.actionOptions.disableAll}
              >
                <IconPlus style={{ width: "70%", height: "70%" }} stroke={2} />
              </ActionIcon>
            </Group>

            {accessArray.fields.length === 0 ? (
              <Grid align="stretch" gutter={"xs"} mb="xs">
                <Grid.Col span={8}>
                  <Divider label="No access" labelPosition="left" />
                </Grid.Col>
              </Grid>
            ) : (
              <>
                {accessArray.fields.map((field, index) => {
                  return (
                    <AccessControl
                      name={`access.${index}`}
                      index={index}
                      control={control}
                      accessArray={accessArray}
                      roleFormState={roleFormState}
                      key={field.__key}
                    />
                  );
                })}
              </>
            )}
          </Stack>

          <Stack gap="0" mb="lg">
            <Group mb="xs" gap={"xs"} style={{ alignItems: "center" }}>
              <Flex>
                <Title
                  order={5}
                  style={{
                    padding: "0px 5px",
                    backgroundColor: "#16A596",
                    color: "#fff",
                  }}
                >
                  Policies
                </Title>
              </Flex>
            </Group>

            <Grid align="stretch" gutter={"xs"} mb="lg">
              <Grid.Col span={8}>
                <CMultiSelect
                  name={"definition.policies"}
                  control={control}
                  searchable
                  data={getPoliciesLOV(policies)}
                  disabled={roleFormState.actionOptions.disableAll}
                />
              </Grid.Col>
            </Grid>
          </Stack>
        </div>

        {(Object.keys(errors).length > 0 || roleFormState.error) && (
          <div style={styles.errors} id="errors">
            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormStateError errors={errors} />
            </div>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <FormSubmitError error={roleFormState.error} />
            </div>
          </div>
        )}

        <Grid align="stretch" gutter={"xs"} columns={24} mt="lg" mb={"lg"}>
          <>
            {(roleFormState.actionOptions.allowedActions || []).map(
              (action: string) => {
                if (action === "save") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        fullWidth
                        type="submit"
                        color="teal"
                        aria-label="Save"
                        loading={roleFormState.submitting}
                      >
                        Save
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "cancel") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        fullWidth
                        component={Link}
                        to={`/app/roles?entityId=${entityId}`}
                        aria-label="Cancel"
                      >
                        Cancel
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "back") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        fullWidth
                        component={Link}
                        to={`/app/roles?entityId=${entityId}`}
                        aria-label="Back"
                      >
                        Back
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "edit") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        aria-label="Edit"
                        fullWidth
                        onClick={() => {
                          roleFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "EDIT",
                          });
                          roleFormDispatch({
                            type: "ACTION-OPTIONS",
                            value: getActionOptions(
                              "edit",
                              dataset,
                              dataset,
                              applicationState
                            ),
                          });

                          setAudit({
                            status: dataset?.status,
                            authStatus: "UNAUTH",
                            usableStatus: dataset?.usableStatus,
                            makerId: applicationState.user.userId,
                            makerDate: new Date(),
                            modificationNo:
                              dataset?.authStatus === "UNAUTH"
                                ? dataset?.modificationNo
                                : (dataset?.modificationNo || 0) + 1,
                            checkerId: "",
                            checkerDate: undefined,
                          });
                        }}
                      >
                        Edit
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "auth") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        aria-label="Auth"
                        fullWidth
                        onClick={async () => {
                          roleFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "AUTH",
                          });

                          try {
                            let payload = {
                              ...dataset,
                              entityId,
                              metadata: {
                                action: "auth",
                              },
                            };
                            await postRole(entityId, payload);
                            // setError("Form", "Unable to Submit");
                            roleFormDispatch({
                              type: "CURRENT-ACTION",
                              value: "VIEW",
                            });

                            roleFormDispatch({
                              type: "SET-SUCCESS",
                              value: "Role Authorized Successfully",
                            });
                          } catch (error: any) {
                            // console.log("post catch", error);
                            roleFormDispatch({
                              type: "SET-ERROR",
                              value:
                                error?.response?.data?.message ||
                                "Unable to Authorize Role",
                            });
                          }
                        }}
                      >
                        Auth
                      </Button>
                    </Grid.Col>
                  );
                } else if (action === "delete") {
                  return (
                    <Grid.Col span={3} key={action}>
                      <Button
                        variant="default"
                        aria-label="Delete"
                        fullWidth
                        onClick={async () => {
                          roleFormDispatch({
                            type: "CURRENT-ACTION",
                            value: "DELETE",
                          });

                          try {
                            let payload = {
                              ...dataset,
                              entityId,
                              metadata: {
                                action: "delete",
                              },
                            };
                            await postRole(entityId, payload);
                            // setError("Form", "Unable to Submit");
                            roleFormDispatch({
                              type: "CURRENT-ACTION",
                              value: "VIEW",
                            });

                            roleFormDispatch({
                              type: "SET-SUCCESS",
                              value: "Role Deleted Successfully",
                            });
                          } catch (error: any) {
                            // console.log("post catch", error);
                            roleFormDispatch({
                              type: "SET-ERROR",
                              value:
                                error?.response?.data?.message ||
                                "Unable to Delete Role",
                            });
                          }
                        }}
                      >
                        Delete
                      </Button>
                    </Grid.Col>
                  );
                } else {
                  return null;
                }
              }
            )}
          </>
        </Grid>

        <Audit audit={audit} />
        {roleFormState.successMessage && (
          <Success message={roleFormState.successMessage} onOk={onOk} />
        )}
      </form>
    </RoleCVMContext.Provider>
  );
}
